import AddIcon from "@mui/icons-material/Add"
import { Button, CircularProgress, IconButton } from "@mui/material"
import { GridColDef } from "@mui/x-data-grid"
import { useQueryClient } from "@tanstack/react-query"
import dayjs from "dayjs"
import timezone from "dayjs/plugin/timezone"
import { toJS } from "mobx"
import { observer } from "mobx-react"
import Image from "next/image"
import { Fragment, useEffect, useMemo, useState } from "react"
import { FormProvider, useFieldArray, useForm } from "react-hook-form"
import DeleteChipIcon from "@/assets/images/Common/DeleteChipIcon.svg"
import Loader from "../Loader/Loader"
import styles from "./AdvancedFilter.module.css"
import { IField } from "@/utils/types/FormGenerator"
import { ModuleName } from "@/utils/store/AppStore.types"
import AppStore from "@/utils/store/AppStore"
import { ExtendedGridColDef } from "@/utils/types/InfiniteTable"
import Dropdown from "@/components/FormGenerator/FormFields/Dropdown/Dropdown"
import DatePicker from "@/components/FormGenerator/FormFields/DatePicker/DatePicker"
import TextField from "@/components/FormGenerator/FormFields/TextField/TextField"

var utc = require("dayjs/plugin/utc")
var customParseFormat = require("dayjs/plugin/customParseFormat")
dayjs.extend(utc)
dayjs.extend(customParseFormat)
dayjs.extend(timezone)

const OperatorData: IField = {
  keyword: "operator",
  label: "Operator",
  field_type: "dropdown",
  answer_value: "and",
  can_edit: true,
  required: true,
  options: [
    {
      label: "AND",
      value: "and",
    },
    {
      label: "OR",
      value: "or",
    },
  ],
}

const ColumnNames: IField = {
  keyword: "column",
  label: "Column",
  field_type: "dropdown",
  can_edit: true,
  required: true,
  options: [],
}

const DropdownData: IField = {
  keyword: "value_dd",
  label: "Values",
  field_type: "dropdown",
  multi_select: true,
  answer_value: "",
  can_edit: true,
  required: true,
  options: [],
}

const FreeSoloData: IField = {
  keyword: "value_dd",
  label: "Type Value, Comma Seperated, 3 Characters",
  field_type: "dropdown",
  multi_select: true,
  comma_tags: true,
  can_edit: true,
  required: true,
  options: [],
}

const DatePickerFromData: IField = {
  keyword: "value",
  label: "From Date",
  field_type: "date",
  answer_value: dayjs.utc().format("DD-MM-YYYY"),
  can_edit: true,
  required: true,
}

const DatePickerToData: IField = {
  keyword: "value",
  label: "To Date",
  field_type: "date",
  answer_value: dayjs.utc().format("DD-MM-YYYY"),
  can_edit: true,
  required: true,
}

const TextFieldData: IField = {
  keyword: "value",
  field_type: "text",
  label: "Type Value",
  can_edit: true,
  required: true,
}

type TFilters = {
  columns: Array<ExtendedGridColDef>
  moduleName?: ModuleName
  onChangeAdvFilter: Function
}

interface TKeyValuePair {
  [field: string]: ExtendedGridColDef
}

// window.dayjs = dayjs

const AdvancedFilter = ({ columns, moduleName, onChangeAdvFilter }: TFilters) => {
  const defaultFilter = [...toJS(AppStore.advancedFilterDef)]

  const [isSubmitLoading, setIsSubmitLoading] = useState<boolean>(false)
  const [isClearLoading, setClearLoading] = useState<boolean>(false)
  const [refreshField, setRefreshField] = useState<boolean>(true)
  const queryClient = useQueryClient()

  const FilterForm = useForm({
    defaultValues: {
      // If Filters persisited based on module then prefill else reset to defaults
      filters: moduleName
        ? toJS(AppStore.advancedFilter[moduleName]).length > 0
          ? toJS(AppStore.advancedFilter[moduleName])
          : defaultFilter
        : defaultFilter,
    },
  })

  const { control, register, getValues, setValue, watch, handleSubmit, reset, resetField } = FilterForm

  const { fields, append, remove } = useFieldArray({
    control,
    name: "filters",
    shouldUnregister: true,
  })

  // @ts-ignore
  // window.RHF = FilterForm
  // // @ts-ignore
  // window.remove = remove

  useEffect(() => {
    // This state is needed as the type of field is getting switched while prefilling
    // By default it is dropdown. This will cause issue for DatePicker and any "Non Dropdown" field
    // This state will cause a Force Re-render while showing a Loader to user to cover both usecase
    setTimeout(setRefreshField, 1000, false)
  }, [])

  const memoizeColumns = useMemo(() => {
    const keyValuePair: TKeyValuePair = {}
    const optionList = columns
      .filter((x: ExtendedGridColDef) => x.filterKey)
      .map((x: ExtendedGridColDef): any => {
        keyValuePair[x?.filterKey || x.field] = x
        return {
          label: x.headerName,
          value: x?.filterKey || x.field,
        }
      })

    return { optionList, keyValuePair }
  }, [])

  const keyValuePair = memoizeColumns.keyValuePair
  ColumnNames.options = memoizeColumns.optionList

  const onClickAddFilter = () => {
    if (fields.length >= 30) return false
    append(AppStore.advancedFilterDef)
  }

  const onClickClearFilter = () => {
    setClearLoading(true)
    if (moduleName) {
      AppStore.setAdvancedFilter(moduleName, AppStore.advancedFilterDef)
      AppStore.setAdvancedFilterCache(moduleName, {})
    }

    remove()
    onClickAddFilter()
    onChangeAdvFilter({})
    setTimeout(setClearLoading, 1000, false)
  }

  const onSubmit = async (data: any) => {
    setIsSubmitLoading(true)
    console.clear()
    const filters = data.filters
    if (moduleName) {
      // If moduleName exists then persisit data
      AppStore.setAdvancedFilter(moduleName, filters)
    }
    // console.table(data.filters)

    const formattedObject = await filters.reduce((acc: any, condition: any) => {
      const { column, value, range, operator } = condition
      // if (Array.isArray(value) && value.every((item) => item.hasOwnProperty("label") && item.hasOwnProperty("value"))) {
      //   acc[column] = value.map((item) => item.value).join(", ")
      // }
      if (Array.isArray(value) && value.every((item) => item.hasOwnProperty("label") && item.hasOwnProperty("value"))) {
        if (acc[column]) {
          // Merges only array of object with same keys
          // @ts-ignore
          const mergedValues = [...new Set([...acc[column], ...value.map((item) => item.value)])]
          acc[column] = mergedValues
        } else {
          acc[column] = value.map((item) => item.value)
        }
      } else if (range && range.start && range.end && dayjs(range.start).isValid() && dayjs(range.end).isValid()) {
        // console.info("Trigger Time :", value)
        // console.info("keyValuePair :", keyValuePair[column]?.["outputFormat"])
        const isLocalOffset = keyValuePair[column]?.["outputFormat"] === "localOffsetTS"
        const offset = dayjs().utcOffset()
        const format = "DD-MM-YYYY"
        const start = dayjs(range.start).format(format)
        const end = dayjs(range.end).format(format)
        const localStart = dayjs(start, format)
        const localEnd = dayjs(end, format)
        // console.info("Trigger Time :", localStart.format("DD-MM-YYYY hh:mm:ss A"), localStart.utc().format("DD-MM-YYYY hh:mm:ss A"))
        acc[column] = {
          start: (isLocalOffset ? dayjs(range.start).add(offset, "minute") : localStart.utc()).unix() * 1000,
          end: (isLocalOffset ? dayjs(range.end).add(offset, "minute") : localEnd.utc()).add(1, "day").subtract(1, "second").unix() * 1000,
        }
      } else {
        acc[column] = value
      }
      if (operator) acc.operator = operator
      // console.info("Tweaker :", operator)
      return acc
    }, {})

    // console.info("Filters :", formattedObject)
    if (moduleName) {
      AppStore.setAdvancedFilterCache(moduleName, formattedObject)
    }
    onChangeAdvFilter(formattedObject)

    setTimeout(setIsSubmitLoading, 1500, false)
  }

  return (
    <FormProvider {...FilterForm}>
      <form className={styles.FormRoot} onSubmit={handleSubmit(onSubmit)}>
        <div className={styles.FormContainer}>
          {refreshField && (
            <div className={styles.FormRefresh}>
              <Loader loading={refreshField} type="GLoader" />
            </div>
          )}
          {fields.map((item, rowIndex) => {
            const columnWatcher: string = watch(`filters.${rowIndex}.column`)
            const isDateRange: boolean = keyValuePair[columnWatcher]?.type === "date"
            // @ts-ignore
            const valueOptions: Array<IFieldOptions> = keyValuePair[columnWatcher]?.valueOptions
            const isTextOnly: boolean = keyValuePair[columnWatcher]?.type === "string"
            // console.info("Loop :", item)
            return (
              <Fragment key={item.id}>
                {/* <div className={styles.WhereWolfField}>
                  {rowIndex === 0 ? (
                    <div className={styles.WhereWolf}>
                      Where
                      <input type="hidden" {...register(`filters.0.operator`)} />
                    </div>
                  ) : (
                    <>
                      {rowIndex === 1 ? (
                        <Dropdown
                          formKey={`filters.${0}.operator`} //
                          form={{ ...OperatorData, id: item.id }}
                          onChange={() => {
                            fields.forEach((elem, i) => {
                              if (i === 0) return false
                              setValue(`filters.${i}.operator`, getValues(`filters.${0}.operator`))
                            })
                          }}
                          displayEmpty
                        />
                      ) : (
                        <>
                          <div className={styles.AnDORWolf}>{watch(`filters.${0}.operator`)}</div>
                        </>
                      )}
                    </>
                  )}
                </div> */}

                <div className={styles.ColumnsField}>
                  <Dropdown
                    formKey={`filters.${rowIndex}.column`} //
                    form={{ ...ColumnNames, id: item.id }}
                    onChange={(curr: any, prev: any) => {
                      const prevVal = prev?.value ? prev.value : prev
                      const prevType = keyValuePair?.[prevVal]?.type

                      const newVal = curr.value
                      const newType = keyValuePair?.[newVal]?.type
                      // console.info("Prev :", newType, prevType)

                      if (newType !== prevType || prevType === "singleSelect") {
                        // Only reset when the types of current and previous are different
                        setValue(`filters.${rowIndex}.value`, "")
                      }
                      // resetField(`filters.${rowIndex}.value`) // Don't reset as it'll have undefined in state wnile UI will have old value
                    }}
                    displayEmpty
                  />
                </div>

                <div className={`${styles.DynamicCondtionFields} ${isDateRange ? styles.isRangePicker : ""}`}>
                  {Array.isArray(valueOptions) ? (
                    <Dropdown
                      formKey={`filters.${rowIndex}.value`} //
                      form={{ ...DropdownData, options: valueOptions, id: item.id }}
                      displayEmpty
                    />
                  ) : (
                    <>
                      {isDateRange ? (
                        <div className={styles.DateRangePickerContainer}>
                          <DatePicker
                            formKey={`filters.${rowIndex}.range.start`}
                            form={{
                              ...DatePickerFromData,
                              id: item.id,
                              answer_value: item.range ? (item.range.start as any) : DatePickerFromData.answer_value,
                            }}
                          />
                          <DatePicker
                            formKey={`filters.${rowIndex}.range.end`}
                            form={{
                              ...DatePickerToData,
                              id: item.id,
                              answer_value: item.range ? (item.range.end as any) : DatePickerToData.answer_value,
                            }} // @ts-ignore
                            minDate={watch(`filters.${rowIndex}.range.start`) || dayjs(DatePickerFromData.answer_value, "DD-MM-YYYY").unix() * 1000}
                          />
                        </div>
                      ) : (
                        <>
                          {isTextOnly ? (
                            <TextField
                              formKey={`filters.${rowIndex}.value`}
                              form={{
                                ...TextFieldData,
                                id: item.id,
                                can_edit: !!getValues(`filters.${rowIndex}.column`),
                                answer_value: item.value ? item.value : TextFieldData.answer_value,
                              }}
                            />
                          ) : (
                            <Dropdown
                              formKey={`filters.${rowIndex}.value`} //
                              form={{
                                ...FreeSoloData,
                                id: item.id,
                                can_edit: !!getValues(`filters.${rowIndex}.column`),
                                answer_value: item.value ? item.value : FreeSoloData.answer_value,
                              }}
                            />
                          )}
                        </>
                      )}
                    </>
                  )}
                </div>
                <div className={styles.RemoveRow}>
                  {fields.length > 1 && (
                    <IconButton onClick={() => remove(rowIndex)}>
                      <Image src={DeleteChipIcon} alt="D" width={14} height={14} />
                    </IconButton>
                  )}
                </div>
              </Fragment>
            )
          })}
        </div>
        <div className={styles.ActionBar}>
          <Button startIcon={<AddIcon />} className={styles.AddFilter} type="button" onClick={onClickAddFilter} disabled={fields.length >= 30}>
            Add Filter
          </Button>
          <div className={styles.FormAction}>
            <Button
              startIcon={isClearLoading && <CircularProgress size="16px" sx={{ color: "white" }} />}
              disabled={isClearLoading || isSubmitLoading}
              className={styles.ClearFilter}
              onClick={onClickClearFilter}
            >
              Clear All
            </Button>
            <Button
              startIcon={isSubmitLoading && <CircularProgress size="16px" sx={{ color: "white" }} />}
              disabled={isSubmitLoading || isClearLoading}
              className={styles.SubmitFilter}
              type="submit"
            >
              Submit
            </Button>
          </div>
        </div>
      </form>
    </FormProvider>
  )
}

export default observer(AdvancedFilter)